import { Popover, PopoverBackdrop, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useFetcher } from '@remix-run/react';
import { GQLCartFragment, GQLOperationalSettingsFragment } from '@theprintbar/graphql';
import { UserSessionData } from '@theprintbar/keystone';
import { WebRoutes, cn, setCartType } from '@theprintbar/shared';
import { BagIcon, LightningBolt } from '@theprintbar/ui';
import { useCallback, useEffect, useState } from 'react';
import { CartCheckout } from '~/components/Cart/CartCheckout';
import { CartEmpty } from '~/components/Cart/CartEmpty';
import { CartItems } from '~/components/Cart/CartItems';
import { CartTaxes } from '~/components/Cart/CartTaxes';
import { getCartQuantity } from '~/utils';

interface CartProps {
	setQuote: (quoteId: string) => void;
	session?: UserSessionData;
}

export function Cart({ setQuote, session }: CartProps) {
	const [oldQuantity, setOldQuantity] = useState<number>();
	const [autoOpen, setAutoOpen] = useState(false);
	const fetcher = useFetcher<GQLCartFragment & { quote?: boolean }>();
	const opsFetcher = useFetcher<GQLOperationalSettingsFragment>();

	useEffect(() => {
		console.log('fetching cart');
		fetcher.load(WebRoutes.API.commerce.cart.get);
	}, [session]);

	useEffect(() => {
		//If there's cart data fetch the operational settings
		if (fetcher.data && !opsFetcher.data) {
			opsFetcher.load(WebRoutes.API.commerce.operationalSettings.get);
		}

		if (fetcher.data?.quote?.id) {
			setQuote(fetcher.data?.quote?.id);
		}
	}, [fetcher.data]);

	const handleFetcherUpdate = useCallback(() => {
		if (fetcher.state === 'idle') {
			setCartType(fetcher.data);
		}

		if (!fetcher.data) {
			return;
		}

		const newQuantity = getCartQuantity(fetcher.data);

		if (newQuantity > (oldQuantity || 0)) {
			//Check for a local storage item that indicates the cart was just updated
			if (!sessionStorage.getItem('cartOpened')) {
				setAutoOpen(true);
				sessionStorage.setItem('cartOpened', 'true');
			} else {
				console.log('Cart already opened');
			}
		}
		setOldQuantity(newQuantity);
	}, [fetcher.data, oldQuantity]);

	useEffect(handleFetcherUpdate, [fetcher.data]);
	return (
		<>
			<Popover className="h-full flex items-center">
				{({ open }) => (
					<>
						{(open || autoOpen) && (
							<PopoverBackdrop onClick={() => setAutoOpen(false)} static className="fixed inset-0 bg-black opacity-30" />
						)}
						<PopoverButton
							as={'button'}
							onClick={e => {
								setAutoOpen(false);
							}}
							className={cn(
								'group focus:outline-none relative z-0 flex items-center overflow-visible py-2 px-2 rounded-full',
								'transition-colors',
								'lg:px-3 border border-transparent lg:border-blue lg:hover:bg-blue lg:hover:text-white',
								(open || autoOpen) && 'bg-blue text-white z-[9999] border-blue',
							)}
						>
							<h6 className="text-xs hidden lg:block mr-1.5">{!!fetcher.data?.quote?.id ? 'Quote' : 'Cart'}</h6>
							{open || autoOpen ? (
								<XMarkIcon className="w-5 h-auto fill-blue lg:group-hover:fill-white" />
							) : (
								<BagIcon className="w-5 h-5 transition-colors fill-blue lg:group-hover:fill-white" />
							)}
							{!!fetcher.data?.items?.length && !open && !autoOpen && (
								<div
									key={`cart-${fetcher.data.id}`}
									className="pointer-events-none w-3.5 h-3.5 lg:w-5 lg:h-5 bg-yellow-200 text-xs text-blue rounded-full flex justify-center items-center border-[1px] border-blue absolute top-0.5 right-0 lg:-top-2 lg:-right-1"
								>
									<span></span>
									{getCartQuantity(fetcher.data)}
								</div>
							)}
						</PopoverButton>
						<Transition
							show={open || autoOpen}
							enter="transition ease-out duration-100"
							enterFrom="transform opacity-0 scale-95"
							enterTo="transform opacity-100 scale-100"
							leave="transition ease-in duration-75"
							leaveFrom="transform opacity-100 scale-100"
							leaveTo="transform opacity-0 scale-95"
						>
							<PopoverPanel
								static
								className={cn(
									'fixed right-2 mx-2 lg:mx-4 z-50 bg-white rounded-3xl overflow-y-auto border-[1px] border-blue w-[94dvw] max-w-lg max-h-[80vh]',
									!!fetcher.data?.quote?.id ? 'top-[6rem] lg:top-[8rem]' : 'top-[4.5rem] lg:top-[7.5rem]',
								)}
							>
								<div className="w-4 h-4 border-l border-t border-blue rotate-45 bg-white absolute -top-[0.55rem] right-24 lg:right-12" />
								<div className="w-full h-full overflow-y-auto">
									<div className="flex flex-col mt-4 pb-1 mx-4 bg-white">
										<div className="flex justify-between items-center">
											<h2 className="font-display text-xl font-light capitalize flex items-center">
												{fetcher.data?.sameday ? (
													<>
														<LightningBolt className="w-6 h-6 -translate-x-1 -translate-y-0.5" />
														Sameday {!!fetcher.data?.quote?.id ? 'quote' : 'cart'}
													</>
												) : !!fetcher.data?.quote?.id ? (
													'Quote'
												) : (
													'Shopping cart'
												)}
											</h2>
											<PopoverButton
												onClick={e => {
													setAutoOpen(false);
												}}
											>
												<XMarkIcon className="h-5 w-5" />
											</PopoverButton>
										</div>
										<div className="flex flex-1 space-between font-belfast font-bold text-xs mt-3">
											<h5 className="flex-1 uppercase text-xs  text-blue">({getCartQuantity(fetcher.data)} items)</h5>
											<h5 className="uppercase text-xs  text-blue">PRICE</h5>
										</div>
									</div>

									{fetcher.state === 'idle' ? (
										!!opsFetcher.data && !!fetcher.data?.items?.length ? (
											<div className="p-4 pt-0 w-full">
												<CartItems
													hasPopover
													settings={opsFetcher.data!}
													cart={fetcher.data}
													quoteSession={!!fetcher.data?.quote?.id}
												/>
												<CartCheckout
													settings={opsFetcher.data!}
													cart={fetcher.data}
													quoteSession={!!fetcher.data?.quote?.id}
												/>
												<CartTaxes settings={opsFetcher.data!} cart={fetcher.data} />
											</div>
										) : (
											<CartEmpty />
										)
									) : (
										<div className="flex justify-center items-center w-full h-full">
											<div className="flex flex-col items-center p-4">
												<div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue" />
												<p className="text-xs mt-3">Loading...</p>
											</div>
										</div>
									)}
								</div>
							</PopoverPanel>
						</Transition>
					</>
				)}
			</Popover>
		</>
	);
}
